import Queue from "p-queue";
import { useState, useEffect, useMemo, useRef } from "react";
import { poll } from "@/lib/api";
import { createAsset } from "@/api/studio/assets/api";
import { Upload } from "@/api/studio/uploads/types/upload.type";
import { stageUpload, uploadUpload, preProcessUpload, getUploadStatus, studyPreProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { Progress } from "@/components/ui/progress";
import { StudiesLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { ReviewMediaSkeleton } from "@/features/studies/components/review-media-skeleton";
import { useStringLoop } from "@/hooks/use-string-loop";
import { useSegment } from "@/hooks/use-segment";
import { useGetStudy } from "@/api/studio/studies/queries";
import { useUploadsStore } from "@/components/ui/drag-drop-upload";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useQueryClient } from "@tanstack/react-query";

/* Prop Types */
interface StudyAssetUploadRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudyAssetUploadRoute component
 *
 * Stages files, uploads them to S3 and triggers pre-processing.
 */
export const StudyAssetUploadRoute = ({ projectId, studyId }: StudyAssetUploadRouteProps) => {
  const isStudyEdit = useFeatureFlag("studyEdit1");

  return isStudyEdit ? <StudyAssetUploadStudyEdit projectId={projectId} studyId={studyId} /> : <StudyAssetUploadLegacy projectId={projectId} studyId={studyId} />;
};

/* ─── Legacy flow (studyEdit1 off) ─── */

const StudyAssetUploadLegacy = ({ projectId, studyId }: StudyAssetUploadRouteProps) => {
  /* Stores */
  const uploads = useUploadsStore((state) => state.uploads);
  const { reset } = useUploadsStore((state) => state.uploadActions);

  /* Queries */
  const { data: study } = useGetStudy(projectId, studyId);

  /* State */
  const [progress, setProgress] = useState(1);
  const [status, setStatus] = useState("waiting");

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-upload");
  const hasStarted = useRef(false);

  /* Queue */
  const queue = useMemo(() => new Queue({ concurrency: 5 }), []);

  /* Side effects */
  useEffect(() => {
    const worker = async (upload: Upload) => {
      // Stage and upload the file in S3
      const { url, key } = await stageUpload(projectId, studyId);
      await uploadUpload({ key, file: upload.file, url }).catch((error) => {
        console.error(`Upload failed for upload.name="${upload.name}", upload.file.name="${upload.file?.name}":`);
        console.log(`uploadUpload({ key: ${key}, url: ${url} })`);
        console.error(error);
        throw error;
      });

      // Add the asset to the study
      await createAsset(projectId, studyId, { pipelineKey: key, name: upload.name, filename: upload.file.name });

      // Pre-process the file to extract regions of interest
      await preProcessUpload(projectId, studyId, key)
        .then(async ({ taskId }) => {
          return await poll(() => getUploadStatus(projectId, studyId, taskId));
        })
        .finally(() => {
          segmentTrackEvent("Media uploaded", {
            projectId,
            studyId,
            studyType: "asset",
            fileName: upload.name,
            useCaseName: study?.useCase.name,
            useCaseId: study?.useCase.id,
          });
        });
    };

    const handleActive = () => setStatus("active");
    const handleEmpty = () => setStatus("empty");
    const handleError = () => setStatus("error");
    const handleNext = () => setProgress((prev) => prev + 1);
    const handleIdle = () => {
      reset();
      navigateToNext();
    };

    queue.on("active", handleActive);
    queue.on("empty", handleEmpty);
    queue.on("error", handleError);
    queue.on("next", handleNext);
    queue.on("idle", handleIdle);

    // Only add tasks to queue once
    if (!hasStarted.current) {
      hasStarted.current = true;
      uploads.forEach((upload) => queue.add(() => worker(upload)));
    }

    return () => {
      queue.off("active", handleActive);
      queue.off("empty", handleEmpty);
      queue.off("error", handleError);
      queue.off("next", handleNext);
      queue.off("idle", handleIdle);
    };
  }, []);

  /* Hooks */
  const displayString = useStringLoop(["Detecting areas of interest...", "Drawing areas of interest on media...", "Running algorithm..."]);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {(status === "active" || status === "empty") && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video src="/videos/review-animation.webm" className="w-48 pb-2" autoPlay muted loop />
            <h2>
              Reviewing {progress} of {uploads.length}
            </h2>
            <Progress value={(progress / uploads.length) * 100} staticColorClass="bg-success" className="w-1/2" />
            <p className="pt-4">{displayString}</p>
          </div>
        </FullPageLoader>
      )}
      <ReviewMediaSkeleton uploadLength={uploads.length} />
    </StudiesLayout>
  );
};

/* ─── New flow (studyEdit1 on) ─── */

const POLL_INTERVAL = 3000;

const StudyAssetUploadStudyEdit = ({ projectId, studyId }: StudyAssetUploadRouteProps) => {
  /* Queries */
  const { data: study } = useGetStudy(projectId, studyId);

  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [isActive, setIsActive] = useState(false);
  const hasStarted = useRef(false);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-upload");
  const qc = useQueryClient();
  const displayString = useStringLoop(["Detecting areas of interest...", "Drawing areas of interest on media...", "Running algorithm..."]);

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Side effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startPreprocessing = async () => {
      setIsActive(true);

      await studyPreProcess(projectId, studyId);
      let finalStatuses: StudyAssetStatus[] = [];

      // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
      while (true) {
        const statuses = await getStudyStatus(projectId, studyId, "preprocess");
        finalStatuses = statuses;
        setAssetStatuses(statuses);

        const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
        if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }

      segmentTrackEvent("Study Processed", {
        projectId,
        studyId,
        studyType: "asset",
        processType: "preprocess",
        totalSucceeded: finalStatuses.filter((status) => status.status === "SUCCEEDED").length,
        totalFailed: finalStatuses.filter((status) => status.status === "FAILED").length,
        useCaseName: study?.useCase.name,
        useCaseId: study?.useCase.id,
      });

      // Invalidate the data cache for the study so that the next step has the latest data
      qc.invalidateQueries({ queryKey: ["pre-processed-data", projectId, studyId] });

      navigateToNext();
    };

    void startPreprocessing();
  }, []);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {isActive && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video src="/videos/review-animation.webm" className="w-48 pb-2" autoPlay muted loop />
            <h2>
              Reviewing {succeededCount} of {totalCount || "..."}
            </h2>
            {totalCount > 0 && <Progress value={(succeededCount / totalCount) * 100} staticColorClass="bg-success" className="w-1/2" />}
            <p className="pt-4">{displayString}</p>
          </div>
        </FullPageLoader>
      )}
      <ReviewMediaSkeleton uploadLength={totalCount} />
    </StudiesLayout>
  );
};
